import {
  categoryType,
  clientColumnsType,
  instrumentType,
  netWorthAPICategoryType,
  netWorthAPICustomClientAndValueAttributionType,
  netWorthAPICustomInstrumentType,
  netWorthAPIDataTypeCurrentNicolaWealthHeldAssetAttributionType,
  sortedClientInfoType,
  totalType
} from "app/modules/net-worth/net-worth.component.types";
import orderBy from "lodash.orderby";


/* SOMEWHAT GENERIC HELPER FUNCTION FOR NET WORTH MODULE*/
const flipObject = (obj: Record<string, string|number>): Record<string, string> => {
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(obj)) {
    result[String(value)] = key;
  }

  return result;
}

/* EXPORTED FUNCTIONS FOR NET WORTH MODULE*/

/*
  ## setProxyForInstrumentHoldings -----------------------------------------------
  Builds stable "proxy" identifiers that unify instrument and holding references across
  three distinct datasets. Proxies are compact labels ("proxy-1", "proxy-2", …) that
  let downstream logic group/merge values for the same logical asset even when different
  sources provide different identifiers (instrumentId or holdingId).

  ## It iterates over the following datasets:
   1) `CustomClientAndValueAttribution` (uses both InstrumentId and HoldingId)
   2) `CurrentNicolaWealthHeldAssetAttribution` (uses HoldingId only; ignores "0")
   3) `CustomInstrument` (uses InstrumentId only)

   ## What it does:
    - Assigns/returns a proxy label for each seen (instrumentId, holdingId) pair using:
      - **Priority:** HoldingId mapping takes precedence over InstrumentId mapping.
      - **Filledness:** An id is considered "filled" unless it is `'null'`, `'0'`, or `''`.
    - Maintains two lookups:
      - `proxyByInstrumentId: Record<instrumentId, proxy>`
      - `proxyByHoldingId:   Record<holdingId, proxy>`
    - The internal counter increases only when encountering a *new* filled id (instrument or holding)
    that does not already map to an existing proxy.

  ## Inputs
  @param CustomClientAndValueAttribution
   Per-client attribution rows containing both InstrumentId and HoldingId. Each row seeds or joins
   to an existing proxy (with HoldingId priority).

  @param CurrentNicolaWealthHeldAssetAttribution
   Current held-asset attribution rows. Only HoldingId is considered; rows with HoldingId `'0'`
   are ignored for proxy creation.

  @param CustomInstrument
   Optional instrument metadata/overrides. Only InstrumentId is considered (HoldingId is treated
   as unfilled for these rows).

  ## Returns
  An object containing the two proxy lookup maps:
  ```ts
  {
   proxyByHoldingId: Record<string, string>,
   proxyByInstrumentId: Record<string, string>
  }
  ```

  ## Behavior & invariants
    - **Deterministic per run:** Proxy labels are assigned in the order items are processed.
   Given identical input order, the same ids receive the same proxy labels.
    - **Non-mutating:** Does not mutate the input arrays.
    - **Id normalization:** Inputs are stringified (`String(id)`) before evaluation.
    - **Filledness rule:** `'null' | '0' | ''` are treated as not filled; everything else is filled.
    - **Backfilling:** When an existing proxy is found via one id, the other id (if filled) is bound
   to the same proxy to keep maps consistent.

  ## Notes
    - HoldingId dominance ensures that when both ids appear, the HoldingId’s existing proxy wins.
    - The defensive `''` return from `getOrCreateProxyId` should be unreachable under normal inputs.
*/
export const setProxyForInstrumentHoldings = (
  CustomClientAndValueAttribution: Array<netWorthAPICustomClientAndValueAttributionType>,
  CurrentNicolaWealthHeldAssetAttribution: Array<netWorthAPIDataTypeCurrentNicolaWealthHeldAssetAttributionType>,
  CustomInstrument: Array<netWorthAPICustomInstrumentType> = [],
) => {
  const proxyByInstrumentId: Record<string, string> = {};
  const proxyByHoldingId: Record<string, string> = {};
  let proxyCounter = 0;

  /*
    ## getOrCreateProxyId is a matching and precedence strategy for proxy generation:
    - If a filled HoldingId already has a proxy, use it and backfill the InstrumentId map if filled.
      - Else if a filled InstrumentId already has a proxy, use it and backfill the HoldingId map if filled.
      - Else if neither id is filled, create a new proxy (`proxy-${++proxyCounter}`), set whichever maps
      are relevant, and return the proxy.
      - If neither id is filled, returns `''` (unexpected path; defensive fallback).
  */
  const getOrCreateProxyId = (instrumentId: string, holdingId: string): string => {
    let existingProxy: string | null = null;
    const consideredFilled = (v: string) => {
      return !(v === 'null' || v === '0' || v === '');
    }

    // Check if a proxy already exists; prioritizes HoldingId over InstrumentId
    if (consideredFilled(holdingId) && proxyByHoldingId[holdingId]) {
      existingProxy = proxyByHoldingId[holdingId];
    } else if (consideredFilled(instrumentId) && proxyByInstrumentId[instrumentId]) {
      existingProxy = proxyByInstrumentId[instrumentId];
    }

    // If a match exists, update any unset mappings, then return the proxy as there's nothing left to do.
    if (existingProxy) {
      if (consideredFilled(holdingId)) {
        proxyByHoldingId[holdingId] ||= existingProxy;
      }
      if (consideredFilled(instrumentId)) {
        proxyByInstrumentId[instrumentId] ||= existingProxy;
      }
      return existingProxy;
    }

    // Otherwise, a match did not exist, so create a new proxyId and set mappings we have data for.
    if (consideredFilled(instrumentId) || consideredFilled(holdingId)) {
      const newProxyId = `proxy-${++proxyCounter}`;
      if (consideredFilled(instrumentId)) {
        proxyByInstrumentId[instrumentId] = newProxyId;
      }
      if (consideredFilled(holdingId)) {
        proxyByHoldingId[holdingId] = newProxyId;
      }

      return newProxyId;
    }

    return ''; // this shouldn't ever happen, but just in case
  };

  CustomClientAndValueAttribution.forEach(va => {
    getOrCreateProxyId(String(va.InstrumentId), String(va.HoldingId));
  });

  CurrentNicolaWealthHeldAssetAttribution.forEach(aa => {
    if (aa.HoldingId.toString() !== '0') {
      getOrCreateProxyId('null', String(aa.HoldingId));

    }
  });
  CustomInstrument.forEach(ci => {
    getOrCreateProxyId(String(ci.InstrumentId), 'null');
  });

  return {proxyByHoldingId, proxyByInstrumentId};
}


/*
  ## orderClients -----------------------------------------------
    Builds a unified, display‑ordered map of all clients referenced in Held Assets and Custom Values.

  ## What it does:
    1. Collect Held‑Asset clients (CurrentNicolaWealthHeldAssetAttribution) and map each
      TargetNameId → ClientOrder (using “first write wins” when multiple entries have the same TargetNameId).

   2. Collect Custom‑Value clients (CustomClientAndValueAttribution). Any entry whose
      TargetNameId is considered missing in the Held‑Asset set is treated as a
      “custom client” and assigned a stable proxy key (i.e - "CUSTOM{ClientOrder}").

   3. Sort the Held‑Asset clients by their ClientOrder and assign each a sequential display
      index. Clients sharing the same ClientName receive the same index so that their values
      will be treated as grouped together (this matches c360 behavior).

   4. Sort custom clients by ClientOrder and append them after all known clients.
      • Custom entries whose TargetNameId already appears among the Held‑Asset results
        are skipped (except for TargetNameId === '0', which is always included). As these
        records are generally from real Held Assets that are merged together (matching
        behaviour in c360) and are not true Custom Values.
      • Each custom entry receives a unique display index and is tagged with
        uniqueKeyType = "clientOrder" to understand its origin for consumer to filter by.

   The final result is a Record keyed by either TargetNameId or proxy key.
   Each entry contains: clientName, targetNameId, display order index, and a uniqueKeyType
   indicating whether it originated from a Held‑Asset client or a Custom‑Value client.
*/
export const orderClients = (
  CurrentNicolaWealthHeldAssetAttribution: Array<netWorthAPIDataTypeCurrentNicolaWealthHeldAssetAttributionType>,
  CustomClientAndValueAttribution: Array<netWorthAPICustomClientAndValueAttributionType>
): Record<string, sortedClientInfoType> => {
  // Map of TargetNameId (as string) -> ClientOrder for all "known" (Held-Asset) clients.
  // Edge case: ClientOrder can be 0; we must not treat 0 as "missing".
  const clientsNameId: Record<string, number> = {};

  // Final output map. Keys are either a TargetNameId (string) or a proxy key like "CUSTOM{ClientOrder}".
  const orderedClients: Record<string, sortedClientInfoType> = {};

  // Step 1: Index known clients by TargetNameId with "first write wins"
  // We want the earliest ClientOrder seen for a TargetNameId to stick.
  for (const { TargetNameId, ClientOrder } of CurrentNicolaWealthHeldAssetAttribution) {
    const key = String(TargetNameId);
    if (!(key in clientsNameId)) {
      // Edge case: Do NOT use truthy checks here; ClientOrder could be 0.
      clientsNameId[key] = ClientOrder;
    }
  }

  // Step 2: Collect custom clients that do not resolve to a known TargetNameId.
  // These entries will be keyed by a stable proxy "CUSTOM{ClientOrder}".
  // Note that we use a membership test (not truthiness) so values like 0 are handled correctly.
  const customClients: Record<string, number> = {};
  for (const { TargetNameId = '', ClientOrder } of CustomClientAndValueAttribution) {
    const tnKey = String(TargetNameId ?? '');
    const isKnownClient =
      // ignore empty TargetNameId
      tnKey !== '' &&
      // treat '0' as not-known in the "known" sense
      tnKey !== '0' &&
      Object.prototype.hasOwnProperty.call(clientsNameId, tnKey);

    if (!isKnownClient) {
      const key = `CUSTOM${ClientOrder}`;
      // First-write wins for custom proxy key; avoids duplicating the same ClientOrder
      if (!(key in customClients)) {
        customClients[key] = +ClientOrder;
      }
    }
  }

  // Step 3: Emit known clients in ClientOrder sequence.
  // Display order is derived by assigning a sequential index.
  // If multiple TargetNameIds share the same ClientName, they get the same display index
  // to group/aggregate their values together (matches c360 behavior).

  // running display index across both known and custom entries
  let index = 0;

  // ClientName -> display index
  const nameToOrder: Record<string, number> = {};
  const sortedKnown = Object.entries(clientsNameId).sort(([, a], [, b]) => a - b);

  for (const [targetNameId] of sortedKnown) {
    // Find the original record to get ClientName (we already know the order from clientsNameId)
    const original = CurrentNicolaWealthHeldAssetAttribution.find(
      c => c.TargetNameId.toString() === targetNameId.toString()
    );
    if (original) {
      const clientName = (original.ClientName || '').trim() || 'Unknown';
      // Edge case: index 0 is valid; use nullish-assignment so the first index per name sticks.
      nameToOrder[clientName] ??= index++;

      orderedClients[targetNameId] = {
        clientName,
        targetNameId,
        order: nameToOrder[clientName],
        uniqueKeyType: 'targetNameId'
      };
    }
  }

  // Step 4: Track which TargetNameIds are already emitted.
  // We skip emitting a custom entry if its TargetNameId already exists in the result,
  // except if the TargetNameId is '0' (we always include custom '0' rows).
  // Edge case: filter out falsy/non-string here, but keep '0' as a valid string for checks later.
  const existingTargetNameIds = new Set<string>(
    Object.values(orderedClients)
      .map(v => v?.targetNameId)
      .filter((id): id is string => typeof id === 'string' && id.length > 0)
  );

  // Step 5: Emit custom clients in ascending ClientOrder, after known clients.
  // Each custom entry gets a new sequential index, and uses its proxy key in the map.
  // Dedup logic:
  //   - If a custom item’s TargetNameId is already present among known results, skip it.
  //   - Special case: allow TargetNameId === '0' to always produce a custom row.
  const sortedCustom = Object.entries(customClients).sort(([, a], [, b]) => a - b);
  for (const [key, orderValue] of sortedCustom) {
    const original = CustomClientAndValueAttribution.find(c => +c.ClientOrder === orderValue);

    // this shouldn't happen but do this to avoid crashing
    if (!original) {
      console.log("CC Skipped.");
      continue;
    }

    // Normalize TargetNameId to a string: '' if nullish (note this is not just falsey), otherwise its string value.
    const originalTargetNameId = original.TargetNameId == null ? '' : String(original.TargetNameId);

    // Skip exact TargetNameId duplicates that already exist from known clients,
    // however, allow '0' as a special case (custom rows with '0' should always appear).
    if (originalTargetNameId !== '0' && originalTargetNameId && existingTargetNameIds.has(originalTargetNameId)) {
      continue;
    }

    // Only write if a proxy key not already present (first write wins).
    if (!orderedClients[key]) {
      const clientName = (original.ClientName || '').trim() || 'Unknown';
      orderedClients[key] = {
        clientName,
        // keep whatever string it is; '' or '0' are both allowed
        targetNameId: originalTargetNameId,
        order: index++,
        uniqueKeyType: 'clientOrder'
      };

      // Mark non-'0' TargetNameIds as seen to avoid duplicates in later custom rows.
      // We intentionally do NOT add '0' here to allow multiple custom rows with '0' across runs.
      if (originalTargetNameId && originalTargetNameId !== '0') {
        existingTargetNameIds.add(originalTargetNameId);
      }
    }
  }

  // Map is keyed by TargetNameId (string) for known clients and by "CUSTOM{ClientOrder}" for custom clients.
  // Each value includes: clientName, targetNameId, display order, and origin (uniqueKeyType).
  return orderedClients;
};


/**
 * ## buildClientNameToOrderMap -----------------------------------------------
   Builds a lookup from **client name → client order** using only entries whose
   `uniqueKeyType` is `'targetNameId'`. This prioritization reflects that
   `targetNameId`-backed names are the canonical, most accurate identifiers for
   sorting and display.

   ## What it does
   - Iterates `orderedClients` and filters to entries where `uniqueKeyType === 'targetNameId'`.
   - Trims `clientName` and skips empty names.
   - Records `map[clientName] = order` using a **first-wins** policy (`||=`) so the
     first occurrence of a name determines its order, preserving upstream ordering stability.
*/
export const buildClientNameToOrderMap = (
  orderedClients: Record<string, sortedClientInfoType>
): Record<string, number> => {
  const map: Record<string, number> = {};

  // prioritize TargetNameId over clientOrder for unique names, as it is more accurate and should be used for sorting.
  for (const key in orderedClients) {
    const entry = orderedClients[key];
    if (!entry || entry.uniqueKeyType !== 'targetNameId') {
      continue;
    }

    const clientName = (entry?.clientName || '').trim() || 'Unknown';
    map[clientName] ||= entry.order;
  }

  return map;
};


/*
## buildTargetNameIdToClientNameMap -----------------------------------------------
  Builds a lookup from **targetNameId → clientName** using only canonical
  entries where `uniqueKeyType === 'targetNameId'`.

  ## What it does
  - Iterates `clientsOrdered` and includes only rows tagged with
      `uniqueKeyType === 'targetNameId'`. This prevents custom/derived clients
    from overwriting real client names in the lookup used for mapping
    CustomValues back to an actual client.
  - Skips entries with missing/empty `clientName`.
  - Uses a **first-wins** policy (`map[id] ||= name`) to keep the earliest valid
    mapping for a given `targetNameId`, preserving upstream order stability.

  ## Rationale
  The resulting map is used to resolve CustomValues matches to real client names.
  Including non-`targetNameId` (e.g., custom) entries risks overwriting canonical
  names with synthetic labels, so they are excluded by design.
  The caller should handle any custom values that do not match a real client name
  with its own custom logic.
*/
export const buildTargetNameIdToClientNameMap = (
  clientsOrdered: Record<string, sortedClientInfoType>
): Record<string, string> => {
  const map: Record<string, string> = {};

  for (const key in clientsOrdered) {
    const entry = clientsOrdered[key];
    // We don't want to include custom clients in this map as this detail as it is used to map CustomValues matches to
    // their actual clientName; by including them here we would run the risk of overwriting a real clientName with a customValue.
    if (!entry || entry.uniqueKeyType !== 'targetNameId') continue;

    // Normalize targetNameId as a string key
    const targetNameId = String(entry.targetNameId);

    map[targetNameId] ||= entry.clientName || 'Unknown';
  }

  return map;
};


/*
  ## setHoldings -----------------------------------------------
  Builds a consolidated mapping of holdings keyed by a generated proxyId to unify data from multiple sources (in the correct priority order).
  Assumes that the same instrumentId or holdingId will never be used for multiple holdings / there will never be data disagreements to that or
  to the intermediate proxy maps as set in `setProxyForInstrumentHoldings`.

  It processes three data sets:
  1. CurrentNicolaWealthHeldAssetAttribution: Adds held assets, initializing display details and totals, and associates each with its proxyId.
  2. CustomInstrument: Adds or updates instrument details (e.g., description, category, order) when they don't already exist from the HeldAsset data.
  3. CustomClientAndValueAttribution: Adds client-specific values to holdings, updates clientOrder when more accurate data is available (as the customValues
  contain "clients" which we would not otherwise see in HeldAssets), and merges amounts for shared names.

  The result is a normalized structure where each proxyId entry contains instrument metadata, client mappings, and aggregated totals for rendering the Net Worth table.
  There is a lot of logic here to ensure that the data is normalized and merged correctly, and that the final result is as accurate as possible; so beware the dragons in the details.
*/
export const setHoldings = (
  clientsOrdered: Record<string, sortedClientInfoType>,
  clientOverrideNameToOrderMap: Record<string, number>,
  targetNameIdToClientNameMap: Record<string, string>,
  CurrentNicolaWealthHeldAssetAttribution: Array<netWorthAPIDataTypeCurrentNicolaWealthHeldAssetAttributionType>,
  CustomClientAndValueAttribution: Array<netWorthAPICustomClientAndValueAttributionType>,
  CustomInstrument: Array<netWorthAPICustomInstrumentType> = [],
  proxyByInstrumentId: Record<string, string> = {},
  proxyByHoldingId: Record<string, string> = {},
  fxRate: number,
) => {
  const holdingsByProxyId: Record<string, instrumentType> = {};
  const convertIfUsd = (amount: number, currencyCode: string) => amount * (currencyCode === 'USD' ? fxRate : 1);

  // Held Assets
  CurrentNicolaWealthHeldAssetAttribution.forEach(aa => {
    if (aa.HoldingId.toString() !== '0') { // NOTE: keep this check to align with the logic in the proxy creation function above: `setHoldingsByProxy`
      const proxyId = proxyByHoldingId[String(aa.HoldingId)];

      // Normalize instrument display name: trim and join only non-empty parts
      const name = (aa.InstrumentName ?? '').trim();
      const acct = (aa.AccountNumber ?? '').trim();
      const instrumentDisplayName = [name, acct].filter(Boolean).join(' ');
      const clientOrder = clientsOrdered[aa.TargetNameId]?.order ?? clientsOrdered['CUSTOM' + aa.ClientOrder]?.order ?? 0;

      // a held asset will only ever have a single value for a single client; even when we are merging by name due to the shared clientOrder,
      // each value goes with just a single instrument/row as only 1 client holds each one in Held Assets.
      // this logic does not apply in the same way for CustomClientAndValueAttribution, as there can be more clients with value against an instrument.
      holdingsByProxyId[proxyId] ||= {
        instrumentDisplayName,
        categoryOrder: aa.CategoryOrder,
        clientOrder,
        instrumentId: '',
        instrumentOrder: 0,
        holdingsByClientOrder: {
          [clientOrder]: {
            value: aa.Amount,
            currencyCode: aa.CurrencyCode
          }
        },
        total: convertIfUsd(aa.Amount, aa.CurrencyCode),
      };
    }
  });

  // Custom Instruments
  CustomInstrument.forEach(ci => {
    const proxyId = proxyByInstrumentId[String(ci.InstrumentId)];

    holdingsByProxyId[proxyId] ||= {
      categoryOrder: ci.CategoryOrder,
      clientOrder: 0, // just an init value for adding later
      holdingsByClientOrder: {},
      instrumentDisplayName: ci.InstrumentDescription,
      instrumentId: ci.InstrumentId,
      instrumentOrder: ci.InstrumentOrder,
      total: 0, // just an init value for adding later
    };

    // Update properties if CustomInstrument provides better info -- unsure if this is actually needed.
    holdingsByProxyId[proxyId].categoryOrder = ci.CategoryOrder;

    if (ci.InstrumentDescription && !holdingsByProxyId[proxyId].instrumentDisplayName) {
      holdingsByProxyId[proxyId].instrumentDisplayName = ci.InstrumentDescription;
    }

    if (ci.InstrumentId && !+holdingsByProxyId[proxyId].instrumentId) {
      holdingsByProxyId[proxyId].instrumentId = ci.InstrumentId;
    }

    if (ci.InstrumentOrder && !+holdingsByProxyId[proxyId].instrumentOrder) {
      holdingsByProxyId[proxyId].instrumentOrder = ci.InstrumentOrder;
    }
  });

  const clientNameToTargetNameIdMap = flipObject(targetNameIdToClientNameMap);

  // Custom (Client) Values
  CustomClientAndValueAttribution.forEach(va => {
    const proxyId = proxyByInstrumentId[String(va.InstrumentId)] ?? proxyByHoldingId[String(va.HoldingId)];
    let newClientOrder = -1;

    if ((Number(va.TargetNameId) > 0)) {
      const proxyNameId = clientNameToTargetNameIdMap[va.ClientName || ''];
      const clientName = targetNameIdToClientNameMap[proxyNameId] ?? clientsOrdered[va.TargetNameId]?.order;

      newClientOrder = (clientOverrideNameToOrderMap[clientName || ''] ?? clientsOrdered[va.TargetNameId]?.order ?? clientsOrdered['CUSTOM' + va.ClientOrder]?.order) ?? -1
    } else {
      newClientOrder = clientsOrdered['CUSTOM' + va.ClientOrder]?.order ?? -1;
    }

    // if any custom values still do not yet have a matching instrumentId flow through from CustomInstruments and have a targetNameId, do not add them to
    // holdingsByInstrumentId as they are from outdated net worth snapshot data in C360 and are now considered a closed account.
    const isAccountClosedInHeldAssetState = !holdingsByProxyId[proxyId]?.instrumentDisplayName && va.TargetNameId;

    if (!isAccountClosedInHeldAssetState) {
      const instrumentName = CustomInstrument.filter(ci => ci.InstrumentId === va.InstrumentId)[0]?.InstrumentDescription;

      holdingsByProxyId[proxyId] ||= {
        categoryOrder: va.CategoryOrder,
        clientOrder: newClientOrder,
        holdingsByClientOrder: {},
        instrumentDisplayName: instrumentName,
        instrumentId: String(va.InstrumentId),
        instrumentOrder: 0,
        total: 0, // just an init value for adding later, always CAD normalized
      };

      const currentInstrument = holdingsByProxyId[proxyId];
      const { total, clientOrder } = currentInstrument;
      let { instrumentId } = currentInstrument;
      instrumentId = String(instrumentId);

      // instrumentId of null means that a HeldAccount is already reporting a more accurate Amount, so don't add it a second time.
      const isAccountNotAlreadyHeld = !(total > 0 && (instrumentId || null) === null);

      // this will overwrite the clientOrder when it is considered to be in a "default" state of `0` and the newClientOrder
      // contains more valuable position data.
      if (clientOrder <= 0 && newClientOrder > clientOrder) {
        holdingsByProxyId[proxyId].clientOrder = newClientOrder;
      }

      if (va.InstrumentId && !+instrumentId) {
        holdingsByProxyId[proxyId].instrumentId = String(va.InstrumentId);
      }

      // For CustomClientAndValueAttribution, we record the Amount only on the first
      // occurrence of each clientOrder. Shared client names produce multiple rows
      // in the database that all represent the same nominal value, so we must ignore
      // later duplicates to avoid inflating the final totals.
      // clientOrder of -1 means that the value should be ignored.
      if (isAccountNotAlreadyHeld && newClientOrder >= 0 && !holdingsByProxyId[proxyId].holdingsByClientOrder[newClientOrder]) {
        holdingsByProxyId[proxyId].holdingsByClientOrder[newClientOrder] = { value: va.Amount, currencyCode: va.CurrencyCode };
        holdingsByProxyId[proxyId].total += convertIfUsd(va.Amount, va.CurrencyCode); // NOTE: total is always CAD normalized
      }
    }
  });

  return holdingsByProxyId;
};

export const setCategories = (
  categoryData: Array<netWorthAPICategoryType>,
  holdings: Record<string, instrumentType>
) => {
  const categories: Record<string, categoryType> = {};

  categoryData.forEach(category => {
    categories[category.CategoryOrder] = {
      categoryName: category.CategoryName,
      categoryOrder: category.CategoryOrder,
      parentName: category.CategoryParentName.toLowerCase(),
      instruments: [],
    };
  });

  // Sorts the holdings by multiple criteria for display purposes, transforming them into an array of objects that include the proxyId for reference.
  const instrumentSort = (holdings: Record<string, instrumentType>) => {
    return orderBy(
      Object.entries(holdings).map(([proxyId, instrument]) => ({ proxyId, ...instrument })),
      ['categoryOrder', 'clientOrder', 'instrumentOrder', 'instrumentDisplayName'],
      ['asc', 'asc', 'asc', 'asc', 'asc']
    );
  };

  const sortedInstruments = instrumentSort(holdings);

  sortedInstruments.forEach((instrument) => {
    const categoryKey = instrument.categoryOrder;
    if (categories[categoryKey]) {
      categories[categoryKey].instruments.push(instrument.proxyId);
    }
  });

  return categories;
};

export const sortCategories = (categoryData: Array<netWorthAPICategoryType>) => {
  const categories: Record<string, number[]> = {};

  categoryData.forEach(category => {
    categories[category.CategoryParentName.toLowerCase()] ||= [];
    categories[category.CategoryParentName.toLowerCase()].push(category.CategoryOrder);
  });

  return categories;
};


/**
  ## setClientColumns -----------------------------------------------
  Produces the set of Client Columns (order and display name) to render, based on:
   - `clientsOrdered`
   - Which client orders actually appear in `holdingsByProxyId`

  ## What it does
   1) Derives the list of valid client-order positions by scanning
      `holdingsByProxyId[*].holdingsByClientOrder` and collecting all unique keys.
      These keys are numeric **strings** (e.g., `"1"`, `"2"`) and are sorted numerically.
   2) Iterates the `clientsOrdered` map and selects candidates that:
      - Have a non-empty `clientName` and a numeric `order` and either:
        - `uniqueKeyType === 'targetNameId'`, or
        - The client's `order` is present in the derived valid positions
          (used to exclude proxies that are effectively “merged” and no longer
          yield a meaningful, distinct client column).
   3) Enforces uniqueness by `clientOrder` (first one wins) and sorts the final list ascending.

   ## Rationale for filtering
   Some client attributions merge into a single proxy for a sum of multiple Held Assets (based on
   a "same name" condition). All individual attributions accumulated in that proxy are excluded;
   such proxies do not correspond to a meaningful standalone `clientName`. Valid positions determined
   from `holdingsByClientOrder` act as the allowlist for non-`targetNameId` entries.

   ## Inputs
   @param clientsOrdered
     A map of client metadata keyed by a string-number orderId. Must contain:
       - `clientName: string` — display name
       - `order: number|string` — numeric order used as the column position
       - `uniqueKeyType?: 'targetNameId' | ...` — if `'targetNameId'`, this client bypasses the valid-position filter.

   @param holdingsByProxyId
     A map of proxyId -> instrument summary. Each item may include:
       - `holdingsByClientOrder: Record<string, unknown>`
         Keys are numeric **strings** for client orders; presence of a key marks that
         client order as “valid” (i.e., produces a meaningful column).

   ## Returns
   A sorted list of client columns (ascending by `clientOrder`):
   ```ts
   Array<{
     clientOrder: number;
     clientName: string;
   }>
   ```

   ## Behavior & invariants
   - **Numeric coercion:** Client orders are coerced via `Number(...)`; non-numeric orders are skipped.
   - **Uniqueness:** Duplicate `clientOrder` values are dropped (keeps the first encountered).
   - **Deterministic within run:** Given stable input order of `clientsOrdered`, output is stable.
   - **No mutations:** Input maps/objects are not mutated.

   ## Notes
   - `getValidClientOrderPositions` considers only the presence of keys in
     `holdingsByClientOrder`; it does not inspect values. An empty object yields no valid positions.
   - The valid-position check applies **only** to non-`targetNameId` entries; `targetNameId`
     clients are always included when name/order are valid.
 */
export const setClientColumns = (
  clientsOrdered: Record<string, sortedClientInfoType>,
  holdingsByProxyId: Record<string, instrumentType>
): clientColumnsType[] => {
  /* getValidClientOrderPositions collects all unique positions present in holdingsByClientOrder
  // Returns numeric-string keys sorted numerically (e.g., ["1","2","3"]). */
  const getValidClientOrderPositions = (
    items: Record<string, { holdingsByClientOrder: Record<string, unknown> }>
  ): string[] => {
    const set = new Set<string>();
    for (const item of Object.values(items)) {
      for (const key of Object.keys(item.holdingsByClientOrder ?? {})) {
        set.add(key);
      }
    }
    return Array.from(set).sort((a, b) => Number(a) - Number(b));
  };

  // This filtering applies only to uniqueKeyTypes based on customOrder.
  //
  // Some proxy names must be excluded from the column set when their values are "merged" —
  // meaning the resulting column would no longer have a meaningful clientName.
  // We detect which proxies to exclude by checking which clientOrders contain attributions
  // when treated as custom (derived from holdingsByClientOrder). Any proxy that ends up
  // merged through this logic must be filtered out.
  const validClientOrderPositions = getValidClientOrderPositions(holdingsByProxyId);

  // Collect candidates (skip empties and invalid orders) and ensure uniqueness by clientOrder
  const result: clientColumnsType[] = [];
  const seenOrders = new Set<number>(); // prevents duplicates by clientOrder

  for (const client of Object.values(clientsOrdered)) {
    const name = client.clientName;
    const orderNum = Number(client.order);

    if (Number.isNaN(orderNum)) continue;

    const isAllowed =
      client.uniqueKeyType === 'targetNameId' ||
      validClientOrderPositions.includes(String(client.order));

    if (!isAllowed) continue;

    // Enforce uniqueness by clientOrder
    if (seenOrders.has(orderNum)) continue;
    seenOrders.add(orderNum);

    result.push({ clientOrder: orderNum, clientName: name || 'Unknown' });
  }

  // Sort by numeric order ascending
  result.sort((a, b) => a.clientOrder - b.clientOrder);

  return result;
};


/*
  ## setTotals -----------------------------------------------
  Produces the Net Worth totals for each category, parent group, and overall.
  This is a separate function to allow for easier unit testing.

  ## Returns
  { totalsByCategory, totalsByParent, netWorthTotals } for rendering summary data

  ## Notes
  Converts non-CAD amounts using the FX rate, sums per-client and overall values, and subtracts liabilities from the final total.
 */
export const setTotals = (
  holdingsByProxyId: Record<string, instrumentType>,
  categorySort: Record<string, number[]>,
  fx: number
) => {
  const totalsByCategory: Record<string, totalType> = {};
  const totalsByParent: Record<string, totalType> = {};
  const netWorthTotals: totalType = {
    total: 0,
    totalByClientOrder: {}
  };

  Object.keys(categorySort).forEach((parentName) => {
    totalsByParent[parentName] ||= {
      total: 0,
      totalByClientOrder: {}
    };

    Object.values(holdingsByProxyId).forEach(instrument => {
      totalsByCategory[instrument.categoryOrder] ||= {
        total: 0,
        totalByClientOrder: {}
      };

      if (categorySort[parentName].includes(instrument.categoryOrder)) {
        Object.entries(instrument.holdingsByClientOrder).forEach(([clientOrder, {value, currencyCode}]) => {
          // Convert non-CAD amounts for accumulated totals using the FX rate
          // the individual amount itself is converted in the template.
          const convertIfUsd = (amount: number, currencyCode: string) => amount * (currencyCode === 'USD' ? fx : 1);
          const convertedAmount = convertIfUsd(value, currencyCode);

          totalsByParent[parentName].totalByClientOrder[clientOrder] ||= 0;
          totalsByParent[parentName].totalByClientOrder[clientOrder] += convertedAmount;
          totalsByParent[parentName].total += convertedAmount;
          totalsByCategory[instrument.categoryOrder].totalByClientOrder[clientOrder] ||= 0;
          totalsByCategory[instrument.categoryOrder].totalByClientOrder[clientOrder] += convertedAmount;
          totalsByCategory[instrument.categoryOrder].total += convertedAmount;
        });
      }
    });
  });

  Object.entries(totalsByParent).forEach(([parentName, parentValue]) => {
    if (parentName !== 'liability') {
      Object.entries(parentValue.totalByClientOrder).forEach(([clientOrder, amount]) => {
        netWorthTotals.totalByClientOrder[clientOrder] ||= 0;
        netWorthTotals.totalByClientOrder[clientOrder] += amount;
        netWorthTotals.total += amount;
      });
    } else {
      Object.entries(parentValue.totalByClientOrder).forEach(([clientOrder, amount]) => {
        netWorthTotals.totalByClientOrder[clientOrder] ||= 0;
        netWorthTotals.totalByClientOrder[clientOrder] -= amount;
        netWorthTotals.total -= amount;
      });
    }
  });

  return { totalsByCategory, totalsByParent, netWorthTotals };
};
